"use client";

import { isRenderedElement } from "@/lib/ui/rendered-elements";
import { uiHaptics } from "@/lib/ui-haptics";
import {
  classifySwipeIntent,
  composeTranslate,
  releaseVelocity,
  SWIPE_BACK_COMMIT_PROGRESS,
  SWIPE_BACK_EDGE_PX,
  SWIPE_BACK_LONG_PRESS_MS,
  shouldCommitSwipeBack,
} from "./swipe-back-math";
import {
  currentSwipeBackTarget,
  markSwipeBackCommitted,
  type SwipeBackTarget,
} from "./swipe-back-registry";
import { localPreviewHref, trackViewportPreviews } from "./viewport-previews";

/**
 * Interactive edge-swipe-back for the native app — the iOS "drag the page
 * off to the right" gesture, built in the web layer so it behaves the same on
 * iOS and Android and goes through the app's own back logic.
 *
 * Why not WKWebView's `allowsBackForwardNavigationGestures`: it walks the
 * WebView's raw history, which is not where back goes here. Every back
 * affordance resolves `?from=` and then pops or pushes through `goBackTo()`
 * (see `src/lib/go-back.ts`), page-level `useBackNavigation` interceptors push
 * dummy same-URL entries, and Next keeps the previous route alive in a hidden
 * `<Activity>` — the native gesture would snapshot-and-pop onto a dummy entry
 * or out of the document. Committing instead runs the on-screen back
 * button's own handler (`swipe-back-registry.ts`), so the swipe and the arrow
 * can never disagree.
 *
 * ## Moving the page without breaking it
 *
 * The page lives in `stage > page` (`SwipeBackStage`). Translating it in place
 * would make `stage` the containing block of every `position: fixed`
 * descendant — footers, sticky CTAs, the tab bar — and, because `stage` is as
 * tall as the document, they would jump to its top or bottom. So the moment
 * the gesture claims the touch, the stage is *frozen*: `stage` becomes a
 * viewport-sized fixed box and `page` is pulled up by the scroll offset with a
 * negative margin. Fixed descendants now resolve against a viewport-sized box,
 * sticky ones against a viewport-sized scrollport, and `<body>` keeps the
 * document's height so `window.scrollY` never moves and no scroll listener
 * hears a thing. Everything reverts before navigation runs.
 *
 * Non-modal page sheets (the hiive and event pages' vaul drawers) are portalled
 * to `<body>`, outside the stage, so they are moved alongside it through the
 * individual `translate` property, which composes with vaul's own `transform`.
 *
 * ## What is underneath
 *
 * Not the real previous page. Next does keep it mounted, but as a
 * `display: none !important` Activity subtree whose effects are torn down and
 * whose inner scroll offsets are already gone; un-hiding it would fight React
 * for that style and show a half-dead page. Instead, the backdrop uses a native
 * viewport snapshot of the explicit back destination, captured while it was
 * visible. Cold links and unavailable/evicted snapshots use the app background.
 */

export type SwipeBackElements = {
  stage: HTMLElement;
  page: HTMLElement;
  backdrop: HTMLElement;
  scrim: HTMLElement;
  indicator: HTMLElement;
  preview: HTMLElement;
};

/** Open UI that owns the screen: the swipe never starts while one is up. */
const MODAL_SELECTOR = [
  "[data-vaul-overlay]",
  '[role="dialog"]:not([data-vaul-drawer])',
  '[role="alertdialog"]',
].join(",");
/**
 * Anchored popups (menus, selects) count only when portalled out of the page;
 * an inline listbox such as a search result list is just page content.
 */
const POPUP_SELECTOR = '[role="menu"], [role="listbox"]';
const TEXT_ENTRY_SELECTOR =
  'input, textarea, select, [contenteditable=""], [contenteditable="true"]';
/** Surfaces that own horizontal drags outright. */
const OPT_OUT_SELECTOR =
  '[data-swipe-back="off"], .maplibregl-map, .leaflet-container';
/** Non-modal vaul sheets that are part of the page (hiive / event pages). */
const PAGE_SHEET_SELECTOR = "[data-vaul-drawer]";
/** Things outside the stage that must slide with it. */
const FOLLOWER_SELECTOR = "[data-vaul-drawer], [data-swipe-back-follow]";

/** How long to wait for the route to change before giving the page back. */
const HANDOFF_TIMEOUT_MS = 1500;
/** After a popstate, how long to wait for React to swap the route in. */
const POP_RENDER_TIMEOUT_MS = 400;
const REVEAL_FADE_MS = 180;
const MAX_SCRIM_OPACITY = 0.28;
/** Spring stiffness (rad/s) for the release; critically damped, ~300ms. */
const SPRING_OMEGA = 22;

type Phase = "idle" | "pending" | "dragging" | "settling" | "handoff";

type Follower = {
  element: HTMLElement;
  inlineTranslate: string;
  computedTranslate: string;
};

type Frozen = {
  scrollY: number;
  bodyMinHeight: string;
  stageStyle: string | null;
  pageStyle: string | null;
};

export function installSwipeBack(elements: SwipeBackElements): () => void {
  const { stage, page, backdrop, scrim, indicator, preview } = elements;

  let phase: Phase = "idle";
  let pointerId = -1;
  let origin: Element | null = null;
  let target: SwipeBackTarget | null = null;
  let startX = 0;
  let startY = 0;
  let startTime = 0;
  let startHref = "";
  let dx = 0;
  let width = 1;
  let armed = false;
  let reduced = false;
  let samples: Array<{ t: number; x: number }> = [];
  let frozen: Frozen | null = null;
  let followers: Follower[] = [];
  let stopSpring: (() => void) | null = null;
  let cleanupHandoff: (() => void) | null = null;
  let windowMoveListening = false;
  let rootUserSelect: { standard: string; webkit: string } | null = null;
  const viewportTracker = trackViewportPreviews(
    page,
    () => phase === "idle" && !isModalUiOpen(stage),
  );

  // ── Start ────────────────────────────────────────────────────────────────

  function onPointerDown(event: PointerEvent) {
    if (!event.isTrusted || event.pointerType !== "touch") return;
    if (phase === "pending" || phase === "dragging") {
      // A second finger: this is a pinch or a fumble, not a swipe.
      abort();
      return;
    }
    if (phase !== "idle" || !event.isPrimary) return;
    if (event.clientX > SWIPE_BACK_EDGE_PX) return;

    const from = event.target instanceof Element ? event.target : null;
    if (!from || !canStartFrom(from, stage)) return;
    if (isModalUiOpen(stage)) return;
    const next = currentSwipeBackTarget();
    if (!next) return;

    phase = "pending";
    pointerId = event.pointerId;
    origin = from;
    target = next;
    startX = event.clientX;
    startY = event.clientY;
    startTime = performance.now();
    startHref = window.location.href;
    dx = 0;
    armed = false;
    samples = [];

    // Touches inside the stage go through its permanent non-passive listener.
    // A page sheet lives outside it, so listen on window for this one touch;
    // vaul's `touch-action: none` already keeps the browser from panning it.
    if (!stage.contains(from)) {
      window.addEventListener("touchmove", onTouchMove, {
        capture: true,
        passive: false,
      });
      windowMoveListening = true;
    }
  }

  // ── Move ─────────────────────────────────────────────────────────────────

  function onPointerMove(event: PointerEvent) {
    if (!event.isTrusted || event.pointerId !== pointerId) return;
    handleMove(event.clientX, event.clientY);
  }

  function onTouchMove(event: TouchEvent) {
    if (phase !== "pending" && phase !== "dragging") return;
    if (event.touches.length > 1) {
      abort();
      return;
    }
    const touch = event.touches[0];
    if (!touch) return;
    handleMove(touch.clientX, touch.clientY);

    if (phase === "dragging") {
      // Ours now: no native scroll, and no carousel / chat row / sheet gesture
      // underneath gets to see the move either.
      if (event.cancelable) event.preventDefault();
      event.stopPropagation();
    } else if (
      phase === "pending" &&
      Math.abs(touch.clientX - startX) >= Math.abs(touch.clientY - startY)
    ) {
      // WebView can claim native panning before our 10px intent threshold.
      // Reserve a rightward edge move while deciding, otherwise a gentle
      // drag gets pointercancel before it can become a back swipe. Vertical
      // moves still pass through to scrolling and sheet pulls.
      if (touch.clientX > startX && event.cancelable) event.preventDefault();
      event.stopPropagation();
    }
  }

  function handleMove(x: number, y: number) {
    if (window.location.href !== startHref) {
      abort();
      return;
    }
    const now = performance.now();
    if (phase === "pending") {
      const intent = classifySwipeIntent(x - startX, y - startY);
      if (intent === "undecided") {
        if (now - startTime > SWIPE_BACK_LONG_PRESS_MS) giveUp();
        return;
      }
      if (intent === "other" || now - startTime > SWIPE_BACK_LONG_PRESS_MS) {
        giveUp();
        return;
      }
      claim();
    }
    if (phase !== "dragging") return;

    dx = clamp(x - startX, 0, width);
    samples.push({ t: now, x });
    if (samples.length > 12) samples.shift();
    render();

    const progress = dx / width;
    if (!armed && progress >= SWIPE_BACK_COMMIT_PROGRESS) {
      armed = true;
      uiHaptics.select();
    } else if (armed && progress < SWIPE_BACK_COMMIT_PROGRESS - 0.05) {
      armed = false;
    }
  }

  function claim() {
    phase = "dragging";
    width = Math.max(1, window.innerWidth);
    reduced = prefersReducedMotion();
    cancelPointerGesturesUnder(origin, pointerId, startX, startY);
    lockSelection();
    if (reduced) {
      indicator.style.top = `${Math.round(startY - 22)}px`;
      indicator.hidden = false;
    } else {
      const image = viewportTracker.previews.get(
        localPreviewHref(target?.getPreviewHref?.() ?? null),
      );
      preview.replaceChildren(...(image ? [image] : []));
      backdrop.dataset.preview = image ? "snapshot" : "fallback";
      followers = collectFollowers(stage);
      freeze();
      backdrop.hidden = false;
    }
    render();
  }

  // ── End ──────────────────────────────────────────────────────────────────

  function onPointerUp(event: PointerEvent) {
    if (!event.isTrusted || event.pointerId !== pointerId) return;
    release();
  }

  function onTouchEnd(event: TouchEvent) {
    if (event.touches.length > 0) return;
    if (phase === "pending" || phase === "dragging") release();
  }

  function onCancel(event: Event) {
    // The synthetic pointercancel we send to gestures underneath is untrusted.
    if (!event.isTrusted) return;
    if (event instanceof PointerEvent && event.pointerId !== pointerId) return;
    // The system took the touch — on Android that is its own back gesture,
    // which navigates through @capacitor/app. Never navigate from here too.
    if (phase === "dragging") settleBack();
    else if (phase === "pending") giveUp();
  }

  function release() {
    if (phase === "pending") {
      giveUp();
      return;
    }
    if (phase !== "dragging" || !target) return;
    const backTarget = target;
    const velocity = releaseVelocity(samples);
    const commit = shouldCommitSwipeBack({ dx, width, velocity });
    finishTouch();
    suppressNextClick();

    if (reduced) {
      indicator.hidden = true;
      resetGestureState();
      if (commit) runBack(backTarget);
      return;
    }
    if (!commit) {
      settleBack(velocity);
      return;
    }
    if (backTarget.mode === "confirm") {
      // The handler asks first; let the question appear over the page.
      settle(0, velocity, () => {
        restoreAll();
        resetGestureState();
        runBack(backTarget);
      });
      return;
    }
    settle(width, velocity, () => handoff(backTarget));
  }

  function settleBack(velocity = 0) {
    finishTouch();
    if (reduced) {
      indicator.hidden = true;
      resetGestureState();
      return;
    }
    settle(0, velocity, () => {
      restoreAll();
      resetGestureState();
    });
  }

  function settle(to: number, velocity: number, done: () => void) {
    phase = "settling";
    stopSpring = springTo(
      dx,
      to,
      velocity,
      (value) => {
        dx = clamp(value, 0, width);
        render();
      },
      () => {
        stopSpring = null;
        done();
      },
    );
  }

  /** The page is off screen: navigate, then let the next page fade in. */
  function handoff(backTarget: SwipeBackTarget) {
    phase = "handoff";
    const fromHref = window.location.href;
    // Back to normal flow BEFORE navigating, so Next's scroll handling and
    // RouteScrollRestorer act on a real document, not a frozen one.
    unfreeze();
    // Page sheets stay parked off screen with the page; `reveal` puts them
    // back (by then a navigated-away route has hidden them anyway).
    stage.style.visibility = "hidden";
    scrim.style.opacity = "0";

    let raf = 0;
    let popped = false;
    let observer: MutationObserver | null = null;
    let renderTimer = 0;
    const onPop = () => {
      popped = true;
    };
    window.addEventListener("popstate", onPop);
    const deadline = window.setTimeout(reveal, HANDOFF_TIMEOUT_MS);

    const finishWait = () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(deadline);
      window.clearTimeout(renderTimer);
      window.removeEventListener("popstate", onPop);
      observer?.disconnect();
      observer = null;
    };
    cleanupHandoff = finishWait;

    const revealNextFrame = () => {
      finishWait();
      raf = window.requestAnimationFrame(reveal);
    };

    const waitForRoute = () => {
      if (window.location.href === fromHref) {
        raf = window.requestAnimationFrame(waitForRoute);
        return;
      }
      if (!popped) {
        // A push: Next writes the URL as the new tree commits, so the page
        // is already there.
        revealNextFrame();
        return;
      }
      // A pop moves the URL first and renders after; wait for the swap.
      observer = new MutationObserver(revealNextFrame);
      observer.observe(page, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["style", "hidden"],
      });
      renderTimer = window.setTimeout(revealNextFrame, POP_RENDER_TIMEOUT_MS);
    };
    raf = window.requestAnimationFrame(waitForRoute);
    // Last, so the popstate listener and the wait are in place first.
    runBack(backTarget);
  }

  function reveal() {
    cleanupHandoff?.();
    cleanupHandoff = null;
    if (phase !== "handoff") return;
    stage.style.opacity = "0";
    stage.style.visibility = "";
    // Commit the transparent frame before transitioning out of it.
    void stage.offsetWidth;
    stage.style.transition = `opacity ${REVEAL_FADE_MS}ms var(--ease-out-strong, ease-out)`;
    stage.style.opacity = "1";
    const timer = window.setTimeout(() => {
      restoreAll();
      resetGestureState();
    }, REVEAL_FADE_MS + 40);
    cleanupHandoff = () => window.clearTimeout(timer);
  }

  function runBack(backTarget: SwipeBackTarget) {
    markSwipeBackCommitted();
    try {
      backTarget.onBack();
    } catch (error) {
      console.error("[swipe-back] back handler threw", error);
      if (phase === "handoff") reveal();
    }
  }

  /** Stop wherever we are and put everything back at once. */
  function abort() {
    stopSpring?.();
    stopSpring = null;
    cleanupHandoff?.();
    cleanupHandoff = null;
    finishTouch();
    restoreAll();
    resetGestureState();
  }

  function giveUp() {
    finishTouch();
    resetGestureState();
  }

  function finishTouch() {
    pointerId = -1;
    if (windowMoveListening) {
      window.removeEventListener("touchmove", onTouchMove, true);
      windowMoveListening = false;
    }
    unlockSelection();
  }

  function resetGestureState() {
    phase = "idle";
    origin = null;
    target = null;
    dx = 0;
    armed = false;
    samples = [];
  }

  // ── Rendering ────────────────────────────────────────────────────────────

  function render() {
    const progress = dx / width;
    if (reduced) {
      // Reduced motion: nothing slides. A still marker fades in instead and
      // fills once a release would go back.
      indicator.style.opacity = String(Math.min(1, progress * 2.5));
      indicator.dataset.armed = String(progress >= SWIPE_BACK_COMMIT_PROGRESS);
      return;
    }
    stage.style.translate = `${dx}px 0px`;
    preview.style.translate = `${-width * 0.25 * (1 - progress)}px 0px`;
    for (const follower of followers) {
      follower.element.style.translate = composeTranslate(
        follower.computedTranslate,
        dx,
      );
    }
    scrim.style.opacity = String(MAX_SCRIM_OPACITY * (1 - progress));
  }

  function freeze() {
    if (frozen) return;
    const scrollY = window.scrollY;
    const probe = firstRenderedChild(page);
    const probeTop = probe?.getBoundingClientRect().top ?? 0;
    frozen = {
      scrollY,
      bodyMinHeight: document.body.style.minHeight,
      stageStyle: stage.getAttribute("style"),
      pageStyle: page.getAttribute("style"),
    };
    // Keep the document as tall as it was, so taking the stage out of flow
    // does not clamp `window.scrollY` (and fire every scroll listener).
    document.body.style.minHeight = `${document.documentElement.scrollHeight}px`;
    const isDark = document.documentElement.classList.contains("dark");
    Object.assign(stage.style, {
      position: "fixed",
      inset: "0px",
      overflow: "hidden",
      translate: "0px 0px",
      willChange: "translate",
      backgroundColor: getComputedStyle(document.body).backgroundColor,
      boxShadow: isDark
        ? "-1px 0 0 rgba(255,255,255,0.08), -12px 0 32px rgba(0,0,0,0.6)"
        : "-1px 0 0 rgba(0,0,0,0.04), -12px 0 32px rgba(15,23,42,0.18)",
    } satisfies Partial<CSSStyleDeclaration>);
    page.style.position = "relative";
    page.style.marginTop = `${-scrollY}px`;
    // Correct for anything the arithmetic missed (a collapsed top margin, an
    // in-flow sibling above the stage) so the first frame is pixel-identical.
    if (probe) {
      const drift = probe.getBoundingClientRect().top - probeTop;
      if (Math.abs(drift) > 0.5) {
        page.style.marginTop = `${-scrollY - drift}px`;
      }
    }
  }

  function unfreeze() {
    if (!frozen) return;
    const { scrollY, bodyMinHeight, stageStyle, pageStyle } = frozen;
    frozen = null;
    restoreStyleAttribute(stage, stageStyle);
    restoreStyleAttribute(page, pageStyle);
    document.body.style.minHeight = bodyMinHeight;
    if (Math.abs(window.scrollY - scrollY) > 1) window.scrollTo(0, scrollY);
  }

  function restoreFollowers() {
    for (const follower of followers) {
      follower.element.style.translate = follower.inlineTranslate;
    }
    followers = [];
  }

  function restoreAll() {
    unfreeze();
    restoreFollowers();
    // The handoff leaves visibility/opacity/transition on the stage after
    // unfreezing; clear whatever is left.
    stage.style.removeProperty("visibility");
    stage.style.removeProperty("opacity");
    stage.style.removeProperty("transition");
    if (!stage.getAttribute("style")) stage.removeAttribute("style");
    backdrop.hidden = true;
    indicator.hidden = true;
    scrim.style.opacity = "";
    preview.replaceChildren();
    preview.style.translate = "";
    delete backdrop.dataset.preview;
  }

  function lockSelection() {
    const { style } = document.documentElement;
    rootUserSelect = {
      standard: style.userSelect,
      webkit: style.getPropertyValue("-webkit-user-select"),
    };
    style.userSelect = "none";
    style.setProperty("-webkit-user-select", "none");
  }

  function unlockSelection() {
    if (!rootUserSelect) return;
    const { style } = document.documentElement;
    style.userSelect = rootUserSelect.standard;
    if (rootUserSelect.webkit)
      style.setProperty("-webkit-user-select", rootUserSelect.webkit);
    else style.removeProperty("-webkit-user-select");
    rootUserSelect = null;
  }

  // ── Interruptions ───────────────────────────────────────────────────────

  function onInterrupt() {
    if (phase === "pending" || phase === "dragging" || phase === "settling")
      abort();
  }

  function onVisibilityChange() {
    if (document.visibilityState === "hidden") onInterrupt();
  }

  const passiveCapture = { capture: true, passive: true } as const;
  window.addEventListener("pointerdown", onPointerDown, passiveCapture);
  window.addEventListener("pointermove", onPointerMove, passiveCapture);
  window.addEventListener("pointerup", onPointerUp, passiveCapture);
  window.addEventListener("pointercancel", onCancel, passiveCapture);
  window.addEventListener("touchend", onTouchEnd, passiveCapture);
  window.addEventListener("touchcancel", onCancel, passiveCapture);
  window.addEventListener("resize", onInterrupt);
  window.addEventListener("popstate", onInterrupt);
  document.addEventListener("visibilitychange", onVisibilityChange);
  // Non-passive so a claimed swipe can stop the page scrolling. Scoped to the
  // stage rather than the document, and it returns immediately unless an edge
  // touch is in flight.
  stage.addEventListener("touchmove", onTouchMove, {
    capture: true,
    passive: false,
  });

  return () => {
    abort();
    viewportTracker.dispose();
    window.removeEventListener("pointerdown", onPointerDown, true);
    window.removeEventListener("pointermove", onPointerMove, true);
    window.removeEventListener("pointerup", onPointerUp, true);
    window.removeEventListener("pointercancel", onCancel, true);
    window.removeEventListener("touchend", onTouchEnd, true);
    window.removeEventListener("touchcancel", onCancel, true);
    window.removeEventListener("resize", onInterrupt);
    window.removeEventListener("popstate", onInterrupt);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    stage.removeEventListener("touchmove", onTouchMove, true);
  };
}

// ── Start guards ───────────────────────────────────────────────────────────

/** Whether a touch on `from` may become a back swipe. Exported for tests. */
export function canStartFrom(from: Element, stage: HTMLElement): boolean {
  // Only the page itself (and its non-modal sheets). Anything else at the
  // edge — a story viewer, search overlay, toast — is portalled outside the
  // stage and owns its own touches.
  if (!stage.contains(from) && !from.closest(PAGE_SHEET_SELECTOR)) return false;
  if (from.closest(TEXT_ENTRY_SELECTOR)) return false;
  if (from.closest(OPT_OUT_SELECTOR)) return false;
  const selection = document.getSelection();
  if (selection && selection.rangeCount > 0 && !selection.isCollapsed)
    return false;
  if (hasScrolledHorizontalAncestor(from, stage)) return false;
  if (isInsideScrolledCarousel(from)) return false;
  return true;
}

export function isModalUiOpen(stage: HTMLElement): boolean {
  if (document.body.style.pointerEvents === "none") return true;
  // `lockRootScroll` (every modal drawer) hides the root overflow.
  if (document.documentElement.style.overflow === "hidden") return true;
  for (const node of document.querySelectorAll(MODAL_SELECTOR)) {
    if (isRenderedElement(node)) return true;
  }
  for (const node of document.querySelectorAll(POPUP_SELECTOR)) {
    if (!stage.contains(node) && isRenderedElement(node)) return true;
  }
  return false;
}

/**
 * A rail the reader has already scrolled: a rightward drag on it means "show
 * me the earlier cards", so the rail keeps it. At `scrollLeft` 0 there is
 * nothing for it to do and the edge swipe wins, as it does on iOS.
 */
function hasScrolledHorizontalAncestor(from: Element, stage: HTMLElement) {
  for (
    let node: Element | null = from;
    node && node !== stage && node !== document.body;
    node = node.parentElement
  ) {
    if (node.scrollLeft <= 1 || node.scrollWidth <= node.clientWidth + 1)
      continue;
    const overflowX = getComputedStyle(node).overflowX;
    if (overflowX === "auto" || overflowX === "scroll") return true;
  }
  return false;
}

/** Same rule for embla, which moves its track with a transform instead. */
function isInsideScrolledCarousel(from: Element) {
  const track = from.closest(
    '[data-slot="carousel-content"]',
  )?.firstElementChild;
  if (!track) return false;
  const transform = getComputedStyle(track).transform;
  if (!transform || transform === "none") return false;
  try {
    return new DOMMatrixReadOnly(transform).m41 < -1;
  } catch {
    return false;
  }
}

// ── Helpers ────────────────────────────────────────────────────────────────

function collectFollowers(stage: HTMLElement): Follower[] {
  const followers: Follower[] = [];
  for (const node of document.querySelectorAll<HTMLElement>(
    FOLLOWER_SELECTOR,
  )) {
    if (stage.contains(node) || !isRenderedElement(node)) continue;
    followers.push({
      element: node,
      inlineTranslate: node.style.translate,
      computedTranslate: getComputedStyle(node).translate,
    });
  }
  return followers;
}

function firstRenderedChild(page: HTMLElement): Element | null {
  for (const child of page.children) {
    if (child.getClientRects().length > 0) return child;
  }
  return null;
}

/**
 * Tell pointer-driven gestures under the finger (chat swipe-to-reply via
 * use-gesture, vaul) that this press is over. Sent at the press's own start
 * point so nothing reads it as a drag, and untrusted so our own listener
 * ignores it.
 */
function cancelPointerGesturesUnder(
  origin: Element | null,
  pointerId: number,
  x: number,
  y: number,
) {
  if (!origin?.isConnected || typeof PointerEvent === "undefined") return;
  origin.dispatchEvent(
    new PointerEvent("pointercancel", {
      bubbles: true,
      cancelable: false,
      pointerId,
      pointerType: "touch",
      isPrimary: true,
      clientX: x,
      clientY: y,
    }),
  );
}

/** Swallow the one click a finished drag could still produce. */
function suppressNextClick() {
  const swallow = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    window.removeEventListener("click", swallow, true);
  };
  window.addEventListener("click", swallow, true);
  window.setTimeout(
    () => window.removeEventListener("click", swallow, true),
    400,
  );
}

function restoreStyleAttribute(element: HTMLElement, value: string | null) {
  if (value === null) element.removeAttribute("style");
  else element.setAttribute("style", value);
}

function prefersReducedMotion() {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

/**
 * Critically damped spring from `from` to `to`, starting at `velocity` px/ms.
 * Closed-form, so a dropped frame lands where it should instead of drifting,
 * with a timer fallback in case the WebView stops delivering frames.
 */
function springTo(
  from: number,
  to: number,
  velocity: number,
  onFrame: (value: number) => void,
  onDone: () => void,
): () => void {
  const c1 = from - to;
  const c2 = velocity * 1000 + SPRING_OMEGA * c1;
  const start = performance.now();
  let raf = 0;
  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    window.cancelAnimationFrame(raf);
    window.clearTimeout(fallback);
    onFrame(to);
    onDone();
  };
  const step = (now: number) => {
    const t = Math.max(0, now - start) / 1000;
    const decay = Math.exp(-SPRING_OMEGA * t);
    const value = to + (c1 + c2 * t) * decay;
    const speed = (c2 - SPRING_OMEGA * (c1 + c2 * t)) * decay;
    onFrame(value);
    if ((Math.abs(value - to) < 0.5 && Math.abs(speed) < 20) || t > 0.9) {
      finish();
      return;
    }
    raf = window.requestAnimationFrame(step);
  };
  raf = window.requestAnimationFrame(step);
  const fallback = window.setTimeout(finish, 1200);
  return () => {
    finished = true;
    window.cancelAnimationFrame(raf);
    window.clearTimeout(fallback);
  };
}
